import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

const VISIBILITY_OPTIONS = ["team", "team_advisor", "team_advisor_investor", "private"];

function VisibilityBadge({ level, t }) {
  const icon = { private: "🔒", team: "👥", team_advisor: "🎯", team_advisor_investor: "📊" }[level] || "👥";
  return (
    <span className="tb-journal-vis" title={t(`journal.visibilityHint.${level}`)} data-testid={`entry-visibility-${level}`}>
      <span aria-hidden="true">{icon}</span>
      <span>{t(`journal.visibility.${level}`)}</span>
    </span>
  );
}

function timeAgo(iso, t) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("journal.relative.justNow");
  if (m < 60) return t("journal.relative.minutesAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("journal.relative.hoursAgo", { n: h });
  const d = Math.floor(h / 24);
  if (d === 1) return t("journal.relative.yesterday");
  return t("journal.relative.daysAgo", { n: d });
}

function EntryHeader({ entry, t }) {
  const initials = (entry.author_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="tb-journal-entry-head">
      <div className="tb-journal-avatar" aria-hidden="true">
        {entry.author_avatar ? <img src={entry.author_avatar} alt="" /> : <span>{initials || "?"}</span>}
      </div>
      <div className="tb-journal-meta">
        <strong data-testid="entry-author-name">{entry.author_name || "—"}</strong>
        <span className="tb-muted"> · {timeAgo(entry.created_at, t)}</span>
        {entry.edited && <span className="tb-muted"> · {t("journal.badges.edited")}</span>}
        {entry.source && entry.source !== "native" && (
          <span className="tb-muted"> · {t(`journal.source.${entry.source}`)}</span>
        )}
      </div>
      <VisibilityBadge level={entry.visibility} t={t} />
    </div>
  );
}

function EntryBody({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const hasFullTranscript = entry.source === "imported_llm" && entry.source_url;
  return (
    <div className="tb-journal-entry-body" data-testid={`entry-body-${entry.id}`}>
      {(entry.content || "").split(/\n\n+/).map((para, i) => (
        <p key={i}>{para}</p>
      ))}
      {hasFullTranscript && (
        <button
          type="button"
          className="tb-ghost-btn tb-mini"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`entry-expand-${entry.id}`}
          style={{ marginTop: 8 }}
        >
          {expanded ? "Hide full transcript" : "View full transcript"}
        </button>
      )}
      {expanded && (
        <TranscriptViewer entryId={entry.id} />
      )}
    </div>
  );
}

function TranscriptViewer({ entryId }) {
  const [full, setFull] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/journal/entries/${entryId}/full`)
      .then((r) => setFull(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e) || "Failed to load"));
  }, [entryId]);
  if (err) return <div className="tb-error" style={{ marginTop: 10 }}>{err}</div>;
  if (!full) return <p className="tb-muted" style={{ marginTop: 10 }}>Loading transcript…</p>;
  const tx = full.imported_transcript || "";
  return (
    <div className="tb-journal-transcript" data-testid={`entry-transcript-${entryId}`}>
      {tx ? (
        <pre style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.5, margin: 0 }}>{tx}</pre>
      ) : (
        <p className="tb-muted" style={{ margin: 0 }}>
          No transcript was captured for this import. ChatGPT share pages render via JavaScript so text extraction often fails — try the &quot;raw paste&quot; method (copy the conversation from ChatGPT and paste as a regular journal entry) or use a Claude share link (usually works).
        </p>
      )}
      {full.source_url && (
        <p style={{ marginTop: 10, fontSize: 12 }}>
          <a href={full.source_url} target="_blank" rel="noreferrer">Open original {full.imported_provider || "conversation"} →</a>
        </p>
      )}
    </div>
  );
}

function EntryActions({ entry, currentUserId, onEdit, onDelete, t }) {
  const isOwn = entry.author_id === currentUserId;
  if (!isOwn) return null;
  return (
    <div className="tb-journal-entry-actions">
      <button
        type="button"
        className="tb-ghost-btn tb-mini"
        onClick={() => onEdit(entry)}
        data-testid={`entry-edit-${entry.id}`}
      >
        {t("journal.actions.edit")}
      </button>
      <button
        type="button"
        className="tb-ghost-btn tb-mini"
        onClick={() => onDelete(entry)}
        data-testid={`entry-delete-${entry.id}`}
      >
        {t("journal.actions.delete")}
      </button>
    </div>
  );
}

function EntryCard({ entry, currentUserId, onEdit, onDelete, onVisibilityChange, t }) {
  return (
    <article className="tb-journal-entry" data-testid={`journal-entry-${entry.id}`}>
      <EntryHeader entry={entry} t={t} />
      <EntryBody entry={entry} />
      <div className="tb-journal-entry-foot">
        <select
          value={entry.visibility}
          onChange={(e) => onVisibilityChange(entry, e.target.value)}
          className="tb-journal-vis-select"
          data-testid={`entry-visibility-select-${entry.id}`}
          disabled={entry.author_id !== currentUserId}
          aria-label="Visibility"
        >
          {VISIBILITY_OPTIONS.map((v) => (
            <option key={v} value={v}>
              {t(`journal.visibility.${v}`)}
            </option>
          ))}
        </select>
        <EntryActions entry={entry} currentUserId={currentUserId} onEdit={onEdit} onDelete={onDelete} t={t} />
      </div>
    </article>
  );
}

function ComposerBox({ t, onPost, initialValue = "", initialVisibility = "team", onCancel, submitting }) {
  const [content, setContent] = useState(initialValue);
  const [visibility, setVisibility] = useState(initialVisibility);
  const [err, setErr] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.focus();
  }, []);

  const submit = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      setErr(t("journal.errors.tooShort"));
      return;
    }
    if (trimmed.length > 20000) {
      setErr(t("journal.errors.tooLong"));
      return;
    }
    setErr("");
    try {
      await onPost({ content: trimmed, visibility });
      setContent("");
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("journal.errors.generic"));
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="tb-journal-composer">
      <textarea
        ref={ref}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t("journal.inputPlaceholder")}
        className="tb-journal-input"
        rows={4}
        data-testid="journal-composer-input"
        disabled={submitting}
      />
      <div className="tb-journal-composer-row">
        <select
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
          className="tb-journal-vis-select"
          data-testid="journal-composer-visibility"
          disabled={submitting}
        >
          {VISIBILITY_OPTIONS.map((v) => (
            <option key={v} value={v}>
              {t(`journal.visibility.${v}`)}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        {onCancel && (
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={onCancel}
            disabled={submitting}
            data-testid="journal-composer-cancel"
          >
            {t("journal.actions.cancel")}
          </button>
        )}
        <button
          type="button"
          className="tb-primary-btn"
          onClick={submit}
          disabled={submitting || !content.trim()}
          data-testid="journal-composer-submit"
        >
          {submitting ? t("journal.posting") : t("journal.post")}
        </button>
      </div>
      {err && (
        <div className="tb-error" data-testid="journal-composer-error">
          {err}
        </div>
      )}
    </div>
  );
}

export default function Journal() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [editing, setEditing] = useState(null); // entry being edited
  const [globalErr, setGlobalErr] = useState("");

  const canJournal = user && (user.role === "admin" || user.features?.journal_enabled === true);

  const load = useCallback(async (cursor) => {
    try {
      const r = await api.get("/journal/entries", {
        params: { limit: 30, ...(cursor ? { before: cursor } : {}) },
      });
      const items = r.data.items || [];
      setEntries((prev) => (cursor ? [...prev, ...items] : items));
      setNextCursor(r.data.next_cursor);
    } catch (e) {
      setGlobalErr(formatApiErrorDetail(e) || t("journal.errors.generic"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!canJournal) {
      setLoading(false);
      return;
    }
    load(null);
  }, [canJournal, load]);

  const handlePost = async ({ content, visibility }) => {
    setPosting(true);
    try {
      const r = await api.post("/journal/entries", { content, visibility });
      setEntries((prev) => [r.data, ...prev]);
    } finally {
      setPosting(false);
    }
  };

  const handleEditSubmit = async ({ content, visibility }) => {
    if (!editing) return;
    setPosting(true);
    try {
      const r = await api.put(`/journal/entries/${editing.id}`, { content, visibility });
      setEntries((prev) => prev.map((e) => (e.id === editing.id ? r.data : e)));
      setEditing(null);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(t("journal.actions.confirmDelete"))) return;
    try {
      await api.delete(`/journal/entries/${entry.id}`);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (e) {
      setGlobalErr(formatApiErrorDetail(e) || t("journal.errors.generic"));
    }
  };

  const handleVisibilityChange = async (entry, next) => {
    try {
      const r = await api.put(`/journal/entries/${entry.id}`, { visibility: next });
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? r.data : e)));
    } catch (e) {
      setGlobalErr(formatApiErrorDetail(e) || t("journal.errors.generic"));
    }
  };

  const grouped = useMemo(() => groupByDay(entries, t), [entries, t]);

  const importShareLink = async () => {
    const url = window.prompt(t("journal.importPrompt"));
    if (!url) return;
    const note = window.prompt(t("journal.importNotePrompt")) || "";
    setPosting(true);
    try {
      const r = await api.post("/journal/import-share-link", { url, note, visibility: "team" });
      setEntries((prev) => [r.data, ...prev]);
    } catch (e) {
      setGlobalErr(formatApiErrorDetail(e) || t("journal.errors.generic"));
    } finally {
      setPosting(false);
    }
  };

  if (!canJournal) {
    return (
      <div className="tb-page tb-journal-page" data-testid="journal-empty-for-role">
        <header className="tb-journal-header">
          <h1>{t("journal.title")}</h1>
        </header>
        <div className="tb-card">
          <p className="tb-muted">{t("journal.emptyForRole")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-journal-page" data-testid="journal-page">
      <header className="tb-journal-header">
        <div>
          <h1>{t("journal.title")}</h1>
          <p className="tb-muted">{t("journal.subtitle")}</p>
        </div>
        <div className="tb-journal-quick-actions">
          <a href="/app/brain" className="tb-ghost-btn" data-testid="journal-goto-brain">
            {t("journal.askBrain")}
          </a>
          <a href="/app/proposals" className="tb-ghost-btn" data-testid="journal-goto-proposals">
            {t("journal.viewProposals")}
          </a>
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={importShareLink}
            disabled={posting}
            data-testid="journal-import-share-link"
          >
            {t("journal.importShareLink")}
          </button>
        </div>
      </header>

      {editing ? (
        <ComposerBox
          t={t}
          initialValue={editing.content}
          initialVisibility={editing.visibility}
          onPost={handleEditSubmit}
          onCancel={() => setEditing(null)}
          submitting={posting}
        />
      ) : (
        <ComposerBox t={t} onPost={handlePost} submitting={posting} />
      )}

      {globalErr && (
        <div className="tb-error tb-journal-global-error" data-testid="journal-global-error">
          {globalErr}
        </div>
      )}

      {loading ? (
        <div className="tb-muted" data-testid="journal-loading">
          {t("journal.loading")}
        </div>
      ) : entries.length === 0 ? (
        <div className="tb-card" data-testid="journal-empty">
          <p className="tb-muted">{t("journal.empty")}</p>
        </div>
      ) : (
        <>
          {grouped.map(({ label, items }) => (
            <section key={label} className="tb-journal-day">
              <h2 className="tb-journal-day-label">{label}</h2>
              <div className="tb-journal-entries">
                {items.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    currentUserId={user.id}
                    onEdit={setEditing}
                    onDelete={handleDelete}
                    onVisibilityChange={handleVisibilityChange}
                    t={t}
                  />
                ))}
              </div>
            </section>
          ))}
          {nextCursor && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <button
                type="button"
                className="tb-ghost-btn"
                onClick={() => load(nextCursor)}
                data-testid="journal-load-more"
              >
                {t("journal.loadMore")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function groupByDay(entries, t) {
  const buckets = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today.getTime() - 86400000);
  const fmt = (d) =>
    d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  for (const e of entries) {
    const d = new Date(e.created_at);
    d.setHours(0, 0, 0, 0);
    let label;
    if (d.getTime() === today.getTime()) label = t("journal.relative.justNow") === "just now" ? "Today" : "Aujourd'hui";
    else if (d.getTime() === yesterday.getTime()) label = t("journal.relative.yesterday");
    else label = fmt(d);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(e);
  }
  return Array.from(buckets.entries()).map(([label, items]) => ({ label, items }));
}
